import React from 'react';
import { CheckCircle, Circle, ChevronRight } from 'lucide-react';

const LEVELS = [
  { level: 1, label: 'Committed', color: 'bg-slate-600', ring: 'ring-slate-600', text: 'text-slate-600', light: 'bg-slate-100' },
  { level: 2, label: 'Employer',  color: 'bg-teal-600',  ring: 'ring-teal-600',  text: 'text-teal-600',  light: 'bg-teal-50'   },
  { level: 3, label: 'Leader',    color: 'bg-amber-500', ring: 'ring-amber-500', text: 'text-amber-600', light: 'bg-amber-50'   },
];

export default function LevelProgressTracker({ currentLevel, maturityScore, levelScores }) {
  const level = currentLevel ?? 0;
  const nextLevelNum = Math.min(level + 1, 3);

  const nextLevel = LEVELS.find(l => l.level === nextLevelNum);

  // Progress toward the next level = the complexity-weighted score for that
  // level's questions (yes = full weight, partial = half weight, no = zero).
  // This replaces the old fixed-threshold mapping that only looked at the
  // overall maturity score, so the journey now reflects how much of the
  // *next level's* requirements — weighted by question complexity — have
  // actually been met.
  const nextLevelScore = levelScores?.[nextLevelNum] ?? 0;
  const progressPct = level >= 3 ? 100 : Math.max(0, Math.min(Math.round(nextLevelScore), 100));

  return (
    <div className="bg-card border border-border rounded-xl p-3 sm:p-5 shadow-sm">
      <div className="mb-3 sm:mb-4">
        <h2 className="text-xs sm:text-sm font-semibold text-foreground">Disability Confident Journey</h2>
        <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
          {level === 0
            ? 'Complete your first assessment to begin'
            : level >= 3
            ? 'Level 3 Leader — highest accreditation achieved'
            : `Level ${level} — ${LEVELS[level - 1]?.label}`}
        </p>
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-0 mb-4 sm:mb-5">
        {LEVELS.map((cfg, idx) => {
          const achieved = level >= cfg.level;
          const isCurrent = level === cfg.level;
          const isNext = level + 1 === cfg.level;
          return (
            <React.Fragment key={cfg.level}>
              <div className="flex flex-col items-center gap-1 sm:gap-1.5 flex-1">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all
                  ${achieved ? `${cfg.color} text-white shadow-md` : isNext ? `bg-white ${cfg.ring} ring-2 ${cfg.text}` : 'bg-muted text-muted-foreground'}`}>
                  {achieved
                    ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                    : <span className="text-xs sm:text-sm font-bold">{cfg.level}</span>}
                </div>
                <div className="text-center">
                  <p className={`text-[10px] sm:text-xs font-semibold ${achieved ? cfg.text : isNext ? cfg.text : 'text-muted-foreground'}`}>
                    L{cfg.level}
                  </p>
                  <p className={`text-[9px] sm:text-[10px] hidden sm:block ${achieved || isNext ? cfg.text : 'text-muted-foreground'}`}>
                    {cfg.label}
                  </p>
                  {isCurrent && (
                    <span className={`text-[9px] sm:text-[10px] font-bold px-1 py-0.5 rounded-full ${cfg.color} text-white mt-0.5 inline-block`}>
                      Current
                    </span>
                  )}
                  {isNext && (
                    <span className={`text-[9px] sm:text-[10px] font-bold px-1 py-0.5 rounded-full ring-1 ${cfg.ring} ${cfg.text} mt-0.5 inline-block`}>
                      Next
                    </span>
                  )}
                </div>
              </div>
              {idx < LEVELS.length - 1 && (
                <div className="flex-1 h-0.5 mb-5 mx-0.5 sm:mx-1 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${LEVELS[idx].color}`}
                    style={{ width: level > cfg.level ? '100%' : level === cfg.level ? `${Math.max(progressPct, 0)}%` : '0%' }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Progress toward next level */}
      {level > 0 && level < 3 && (
        <div className={`rounded-lg p-2 sm:p-3 ${nextLevel?.light}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className={`text-[10px] sm:text-xs font-semibold ${nextLevel?.text}`}>
              Toward L{level + 1} {nextLevel?.label}
            </span>
            <span className={`text-[10px] sm:text-xs font-bold ${nextLevel?.text}`}>{Math.max(progressPct, 0)}%</span>
          </div>
          <div className="w-full bg-white/70 rounded-full h-2 sm:h-2.5 overflow-hidden">
            <div
              className={`h-2 sm:h-2.5 rounded-full transition-all duration-700 ${nextLevel?.color}`}
              style={{ width: `${Math.max(progressPct, 0)}%` }}
            />
          </div>
          <p className="text-[9px] sm:text-[10px] text-muted-foreground mt-1.5">
            Complexity-weighted score: {nextLevelScore}%
          </p>
        </div>
      )}

      {level >= 3 && (
        <div className="rounded-lg p-2 sm:p-3 bg-amber-50 text-center">
          <span className="text-xs font-semibold text-amber-700">🏆 Level 3 Leader achieved</span>
        </div>
      )}

      {level === 0 && (
        <div className="rounded-lg p-2 sm:p-3 bg-muted text-center">
          <span className="text-[10px] sm:text-xs text-muted-foreground">Start assessment to begin</span>
        </div>
      )}
    </div>
  );
}